import {
	createConsentEngine,
	createVendorRegistry,
	type VendorAdapter,
} from "@moznsystems/mozn-ui/consent";
import { IS_SELF_HOSTED } from "@/constants/constants";
import {
	type ClarityTags,
	type ClarityUser,
	identifyClarityUser,
	initClarity,
	isClarityEnabled,
	revokeClarityConsent,
	setClarityTags,
} from "@/services/clarity";
import {
	grantFraudSdkConsent,
	isFraudSdkEnabled,
	revokeFraudSdkConsent,
} from "@/services/fraud-sdk";
import { initHotjar, isHotjarEnabled, teardownHotjar } from "@/services/hotjar";
import {
	identifyUserpilotUser,
	initUserpilot,
	isUserpilotEnabled,
	teardownUserpilot,
	type UserpilotUser,
} from "@/services/userpilot";
import { CONSENT_CATEGORY, CONSENT_CONFIG } from "./config";

export interface TrackedUser {
	clarity: ClarityUser & { tags: ClarityTags };
	userpilot: UserpilotUser;
}

interface ConsentVendor extends VendorAdapter {
	isEnabled: () => boolean;
}

const consentEngine = createConsentEngine(CONSENT_CONFIG);
const vendorRegistry = createVendorRegistry();

let trackedUser: TrackedUser | null = null;
let isStarted = false;
let isConsentEnabled = false;

const identifyClarity = () => {
	if (!trackedUser) return;
	const { tags, ...user } = trackedUser.clarity;
	identifyClarityUser(user);
	setClarityTags(tags);
};

const identifyUserpilot = () => {
	if (trackedUser) identifyUserpilotUser(trackedUser.userpilot);
};

const FRAUD_SDK_VENDOR: ConsentVendor = {
	id: "fraud-sdk",
	category: CONSENT_CATEGORY.FRAUD_PREVENTION,
	isEnabled: isFraudSdkEnabled,
	init: grantFraudSdkConsent,
	teardown: revokeFraudSdkConsent,
};

const SAAS_VENDORS: ConsentVendor[] = [
	{
		id: "clarity",
		category: CONSENT_CATEGORY.ANALYTICS,
		isEnabled: isClarityEnabled,
		init: () => {
			initClarity();
			identifyClarity();
		},
		teardown: revokeClarityConsent,
	},
	{
		id: "hotjar",
		category: CONSENT_CATEGORY.ANALYTICS,
		isEnabled: isHotjarEnabled,
		init: initHotjar,
		teardown: teardownHotjar,
	},
	{
		id: "userpilot",
		category: CONSENT_CATEGORY.FUNCTIONAL,
		isEnabled: isUserpilotEnabled,
		init: () => {
			initUserpilot();
			identifyUserpilot();
		},
		teardown: teardownUserpilot,
	},
];

/**
 * Every non-essential tracker, keyed to the category that gates it. No
 * other code may initialise these SDKs: the registry is the only path that
 * loads them, and only after consent. Self-hosted builds ship none of them.
 */
const CONSENT_VENDORS: ConsentVendor[] = IS_SELF_HOSTED
	? []
	: [FRAUD_SDK_VENDOR, ...SAAS_VENDORS];

const getEnabledVendors = () =>
	CONSENT_VENDORS.filter((vendor) => vendor.isEnabled());

/**
 * Whether to ask for consent: the `enable-cookie-consent` flag is on and this
 * build ships a vendor that needs it. Self-hosted, POC and local builds ship
 * none, so there is nothing to ask for and no banner.
 */
const requiresConsent = () =>
	isConsentEnabled && getEnabledVendors().length > 0;

/** The required categories plus every category gating a vendor this build ships. */
const getActiveCategoryIds = () => {
	const vendorCategories = new Set(
		getEnabledVendors().map((vendor) => vendor.category),
	);
	return CONSENT_CONFIG.categories
		.filter(({ id, required }) => required || vendorCategories.has(id))
		.map(({ id }) => id);
};

const syncVendorsWithConsent = () => {
	let hasWithdrawn = false;
	vendorRegistry.applyWithdrawal((categoryId) => {
		const isAccepted = consentEngine.isAccepted(categoryId);
		if (!isAccepted) hasWithdrawn = true;
		return isAccepted;
	});

	// The vendors' injected scripts and in-memory state survive their
	// teardown, so a withdrawal only takes full effect on a fresh page.
	if (hasWithdrawn) {
		window.location.reload();
		return;
	}

	vendorRegistry.applyConsent(consentEngine.isAccepted);
};

/**
 * Registers the vendors and starts any the user already accepted. Call once,
 * after authentication, with the `enable-cookie-consent` flag. With the flag
 * off every vendor starts without asking, as before consent existed.
 */
const startConsentManagement = ({ isEnabled }: { isEnabled: boolean }) => {
	if (isStarted) return;
	isStarted = true;
	isConsentEnabled = isEnabled;

	for (const { isEnabled: _, ...adapter } of getEnabledVendors()) {
		vendorRegistry.register(adapter);
	}

	if (!isEnabled) {
		vendorRegistry.applyConsent(() => true);
		return;
	}

	vendorRegistry.applyConsent(consentEngine.isAccepted);
	consentEngine.subscribe(syncVendorsWithConsent);
};

/**
 * Hands the signed-in user to the vendors. Identifies immediately on vendors
 * that are already running; the rest identify when consent starts them.
 */
const setTrackedUser = (user: TrackedUser) => {
	trackedUser = user;
	identifyClarity();
	identifyUserpilot();
};

export {
	CONSENT_CATEGORY,
	consentEngine,
	getActiveCategoryIds,
	requiresConsent,
	setTrackedUser,
	startConsentManagement,
};
